

class StackScrollSection {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.mt-stack-scroll-container');
    this.images = section.querySelectorAll('.mt-stack-scroll-image-item');
    this.contentDesktop = section.querySelector('.mt-stack-scroll-content-desktop'); // 桌面端的文本框
    this.contentMobile = section.querySelector('.mt-stack-scroll-content-mobile'); // 移动端的文本框
    this.isAnimating = false;
    this.animationComplete = false;
    
    // 惯性动画相关变量
    this.currentProgress = 0; // 当前实际进度
    this.targetProgress = 0; // 目标进度
    this.lerpSpeed = 0.12; // 提高插值速度，减少惯性效果（0.05-0.15 之间效果较好）
    this.animationFrameId = null;
    
    // 存储每张图片的当前位置和目标位置
    this.imagePositions = Array.from(this.images).map(() => ({
      currentLeft: 0,
      targetLeft: 0
    }));
    
    this.init();
  }

  init() {
    // 检测是否为移动端
    const isMobile = window.innerWidth <= 1024;

    if (isMobile) {
      // 移动端：不创建 sticky wrapper，不绑定滚动动画，页面正常上下滚动
      // 图片区域由 CSS overflow-x + scroll-snap 实现左右滑动
      this.initMobile();
      return;
    }

    // 创建固定包裹器
    this.createStickyWrapper();
    
    // 初始化图片位置
    this.initializeImagePositions();
    
    // 绑定滚动事件
    this.bindEvents();
    
    // 初始化检查
    this.checkScroll();
    
    // 启动惯性动画循环
    this.startInertiaAnimation();
  }

  initMobile() {
    // 移动端初始化：确保容器正常文档流，无需额外处理
    // 样式已由 CSS 控制（overflow-x: auto + scroll-snap）
    // 监听 resize，若切换到桌面端则重新初始化
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1024 && !this._desktopInitialized) {
        this._desktopInitialized = true;
        this.createStickyWrapper();
        this.initializeImagePositions();
        this.bindEvents();
        this.checkScroll();
        this.startInertiaAnimation();
      }
    });
  }

  createStickyWrapper() {
    // 检查是否已经存在 sticky wrapper（HTML 中已经写好的结构）
    let stickyWrapper = this.section.querySelector('.mt-stack-scroll-sticky-wrapper');
    let inner = this.section.querySelector('.mt-stack-scroll-inner');
    
    if (!stickyWrapper || !inner) {
      // 如果不存在，则动态创建（兼容旧版本）
      stickyWrapper = document.createElement('div');
      stickyWrapper.className = 'mt-stack-scroll-sticky-wrapper';
      
      inner = document.createElement('div');
      inner.className = 'mt-stack-scroll-inner';
      
      // 仅移动图片容器和桌面端内容到 inner
      const imagesContainer = this.section.querySelector('.mt-stack-scroll-images');
      const contentContainerDesktop = this.section.querySelector('.mt-stack-scroll-content-desktop');
      
      if (imagesContainer) inner.appendChild(imagesContainer);
      if (contentContainerDesktop) inner.appendChild(contentContainerDesktop);
      stickyWrapper.appendChild(inner);
      
      this.container.appendChild(stickyWrapper);
    }
    
    this.stickyWrapper = stickyWrapper;
    this.inner = inner;
  }

  // 初始化图片位置
  initializeImagePositions() {
    this.images.forEach((image, index) => {
      const imageIndex = parseInt(image.dataset.index);
      const initialLeft = this.getInitialLeft(imageIndex);
      this.imagePositions[index] = {
        currentLeft: initialLeft,
        targetLeft: initialLeft
      };
    });
  }

  bindEvents() {
    // 使用节流优化性能
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.checkScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // 处理窗口大小调整
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // 重置样式以适应新的屏幕尺寸
        this.resetStyles();
        this.checkScroll();
      }, 200);
    });
  }

  resetStyles() {
    // 重置图片容器的样式
    const imagesWrapper = this.section.querySelector('.mt-stack-scroll-images-wrapper');
    if (imagesWrapper) {
      imagesWrapper.style.transform = '';
    }
    
    // 重置桌面端文本内容样式
    if (this.contentDesktop) {
      this.contentDesktop.style.opacity = '';
      this.contentDesktop.style.transform = '';
    }
    
    // 移动端文本框由CSS控制，不需要重置
    
    // 重置每张图片的样式和位置数据
    this.images.forEach((image, index) => {
      image.style.left = '';
      image.style.top = '';
      image.style.transform = '';
      image.style.zIndex = '';
      
      // 重新初始化位置数据
      const imageIndex = parseInt(image.dataset.index);
      const initialLeft = this.getInitialLeft(imageIndex);
      this.imagePositions[index] = {
        currentLeft: initialLeft,
        targetLeft: initialLeft
      };
    });
    
    // 重置进度
    this.currentProgress = 0;
    this.targetProgress = 0;
  }

  checkScroll() {
    const rect = this.section.getBoundingClientRect();
    const sectionTop = rect.top;
    const sectionHeight = rect.height;
    const windowHeight = window.innerHeight;
    
    // 计算滚动进度（0-1）
    // 当section顶部到达视口顶部时开始，当section底部到达视口底部时结束
    let progress = 0;
    
    if (sectionTop <= 0 && sectionTop > -(sectionHeight - windowHeight)) {
      // section 在视口中，计算进度
      const scrollableDistance = sectionHeight - windowHeight;
      
      // 确保有足够的滚动距离，避免除以接近0的数
      if (scrollableDistance > 100) {
        progress = Math.abs(sectionTop) / scrollableDistance;
        progress = Math.max(0, Math.min(1, progress));
      }
      this.isAnimating = true;
    } else if (sectionTop > 0) {
      // section 还没进入
      progress = 0;
      this.isAnimating = false;
      this.animationComplete = false;
    } else {
      // section 已经完全滚过
      progress = 1;
      this.isAnimating = false;
      this.animationComplete = true;
    }

    // 更新目标进度，实际进度将通过惯性动画平滑过渡
    this.targetProgress = progress;
  }

  // 惯性动画循环
  startInertiaAnimation() {
    const animate = () => {
      // 使用线性插值（lerp）平滑过渡到目标进度
      const diff = this.targetProgress - this.currentProgress;
      
      // 只有当差值大于一个很小的阈值时才继续更新
      if (Math.abs(diff) > 0.0001) { // 提高阈值精度
        this.currentProgress += diff * this.lerpSpeed;
        this.updateAnimation(this.currentProgress);
      } else if (Math.abs(diff) > 0) {
        // 当非常接近目标时，直接设置为目标值
        this.currentProgress = this.targetProgress;
        this.updateAnimation(this.currentProgress);
      }
      
      this.animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
  }

  updateAnimation(progress) {
    // 检测是否为移动端 (1024px 以下)
    const isMobile = window.innerWidth <= 1024;
    
    if (isMobile) {
      // 移动端：横向轨道滚动效果
      this.updateMobileAnimation(progress);
    } else {
      // 桌面端：原有的堆叠效果
      this.updateDesktopAnimation(progress);
    }
    
    // 标记动画完成状态
    if (progress >= 0.95) {
      this.section.classList.add('animation-complete');
    } else {
      this.section.classList.remove('animation-complete');
    }
  }

  updateDesktopAnimation(progress) {
    // 将进度映射到不同的阶段
    // 0-0.6: 图片堆叠动画
    // 0.2-0.8: 文本淡入动画
    
    const stackProgress = Math.min(progress / 0.6, 1);
    const fadeProgress = Math.max((progress - 0.2) / 0.6, 0);
    
    // 获取当前窗口宽度，用于计算堆叠偏移量
    const width = window.innerWidth;
    let stackOffsetPerCard;
    
    if (width <= 1060) {
      stackOffsetPerCard = 40; // 768px-1060px: 露出 40px
    } else if (width <= 1280) {
      stackOffsetPerCard = 60; // 1061px-1280px: 露出 60px
    } else if (width < 1920) {
      stackOffsetPerCard = 90; // 1281px-1919px: 露出 90px
    } else {
      stackOffsetPerCard = 120; // >=1920px: 露出 120px
    }
    
    // 使用缓动函数让动画更自然
    const easedProgress = this.easeInOutCubic(stackProgress);
    
    // 更新每张图片的位置（使用惯性效果）
    this.images.forEach((image, index) => {
      const imageIndex = parseInt(image.dataset.index);
      
      // 确保第一张图片也有正确的初始位置
      const startLeft = this.getInitialLeft(imageIndex);
      
      if (imageIndex === 0) {
        // 第一张图片保持在 left: 0 位置不动
        const targetLeft = startLeft - (startLeft - 0) * easedProgress;
        
        // 更新目标位置
        this.imagePositions[index].targetLeft = targetLeft;
        
        // 使用惯性插值计算当前位置
        const currentLeft = this.imagePositions[index].currentLeft;
        const leftDiff = targetLeft - currentLeft;
        this.imagePositions[index].currentLeft += leftDiff * this.lerpSpeed;
        
        image.style.left = `${this.imagePositions[index].currentLeft}px`;
        image.style.top = '50%';
        image.style.transform = 'translateY(-50%)';
        image.style.zIndex = 4;

        // 第一张始终显示文字
        const contant = image.querySelector('.mt-stack-scroll-contant');
        if (contant) contant.style.opacity = '1';
        return;
      }
      
      // 从第二张开始计算堆叠位置（露出部分）
      // 第二张: stackOffsetPerCard * 1
      // 第三张: stackOffsetPerCard * 2
      // 第四张: stackOffsetPerCard * 3
      const stackOffsetX = imageIndex * stackOffsetPerCard;
      const endLeft = stackOffsetX;
      
      // 计算目标位置
      const targetLeft = startLeft - (startLeft - endLeft) * easedProgress;
      
      // 更新目标位置
      this.imagePositions[index].targetLeft = targetLeft;
      
      // 使用惯性插值计算当前位置
      const currentLeft = this.imagePositions[index].currentLeft;
      const leftDiff = targetLeft - currentLeft;
      this.imagePositions[index].currentLeft += leftDiff * this.lerpSpeed;
      
      // 应用变换
      image.style.left = `${this.imagePositions[index].currentLeft}px`;
      image.style.top = '50%';
      image.style.transform = 'translateY(-50%)';
      
      // 调整z-index以确保正确的堆叠顺序（第一张始终在最上面）
      image.style.zIndex = 4 - imageIndex;

      // 折叠后隐藏文字：当该图片向左移动到接近堆叠位置时，逐渐隐藏文字
      // 以图片当前left与初始left的比例来判断折叠程度
      const contant = image.querySelector('.mt-stack-scroll-contant');
      if (contant) {
        // stackProgress > 0.3 时开始淡出，> 0.6 时完全隐藏
        const fadeOutStart = 0.3;
        const fadeOutEnd = 0.6;
        const textOpacity = easedProgress < fadeOutStart
          ? 1
          : easedProgress > fadeOutEnd
            ? 0
            : 1 - (easedProgress - fadeOutStart) / (fadeOutEnd - fadeOutStart);
        contant.style.opacity = textOpacity;
      }
    });
    
    // 更新文本内容的透明度和位置
    if (this.contentDesktop) {
      // 使用更平滑的缓动函数
      const fadeEased = this.easeOutQuart(fadeProgress);
      this.contentDesktop.style.opacity = fadeEased;
      this.contentDesktop.style.transform = `translateX(${50 - 50 * fadeEased}px)`;
    }
  }

  updateMobileAnimation(progress) {
    // 移动端已改为 CSS overflow-x scroll-snap 实现，无需 JS 处理
    return;
  }

  getInitialLeft(index) {
    // 根据屏幕宽度返回初始left值（图片分散开的初始状态，间距统一为8px）
    const width = window.innerWidth;
    
    if (width <= 1024) {
      // 移动端不使用此函数
      return 0;
    } else if (width <= 1060) {
      // 768px-1060px: 图片宽度 240px + 8px 间距 = 248px
      return index * 248;
    } else if (width <= 1200) {
      // 1061px-1200px: 图片宽度 260px + 8px 间距 = 268px
      return index * 268;
    } else if (width < 1920) {
      // 1201px-1919px: 图片宽度 304px + 8px 间距 = 312px
      return index * 312;
    } else {
      // >=1920px: 图片宽度 394px + 8px 间距 = 402px
      return index * 402;
    }
  }

  // 缓动函数
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  // 更平滑的缓动函数 - 用于文本动画
  easeOutQuart(t) {
    return 1 - Math.pow(1 - t, 4);
  }

  // 防抖函数
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// 初始化所有 Stack Scroll 组件
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.mt-stack-scroll-section');
  sections.forEach(section => {
    new StackScrollSection(section);
  });
});

// 支持 Shopify 主题编辑器
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.mt-stack-scroll-section');
    if (section) {
      new StackScrollSection(section);
    }
  });
}
